import { useCallback } from "react";
import { useLocation } from "react-router-dom";

// "I clicked the menu item I'm already on — refresh it."
//
// Client: "One thing that annoys me as a user is that when I'm on the desktop
// app.qushi.io 'Trade' screen, I click the left menu 'Trade' again, thinking it
// will refresh the page and the chart, but it doesn't. It should. How do we make
// the desktop left menu buttons clickable to refresh instead of going dead?"
//
// They are dead by design, not by accident: a react-router <NavLink> pointing at
// the location you are already on performs no navigation at all, so nothing
// re-renders and nothing refetches. Every row in both left menus behaves this
// way, Trade is simply the one where it is most obvious, because the chart is
// the thing you most want to re-pull.
//
// A full reload rather than a soft re-render, deliberately. "Refresh the page
// and the chart" is what the client asked for, and the Trade screen holds a
// great deal of state that is not derived from the route — the chart instance,
// its socket, the order ticket, the watchlist rail. A router-level trick
// (re-pushing the same path, or keying the outlet on location.key) would remount
// SOME screens and quietly miss others, and keying the outlet would also throw
// away state on every ordinary navigation, which nobody asked for. A reload is
// the one thing that means the same for every screen.
//
// Lives in its own file because BOTH left menus need it and they are different
// components: the main app's Sidebar and the league's LeagueMenu. One
// definition, so "clicking where you already are refreshes" cannot end up true
// in one menu and false in the other.

// Is the browser ALREADY at exactly this address?
//
// Exact match only — deliberately NOT NavLink's `end`-less "is active" rule,
// which also counts child routes. Those are two different questions:
//
//   highlighted?  /contest is "active" while you are on /contest/my-games, so
//                 the League Play row stays lit inside its own section.
//   dead click?   it is NOT dead there — clicking it navigates UP to /contest,
//                 which is exactly what the user wants.
//
// Reloading on a prefix match would strand someone on /contest/my-games every
// time they tried to get back to League Play. So the refresh only fires where
// the router genuinely does nothing: same path, same page.
export const isSameScreen = (pathname, to) => {
  if (!to) return false;
  const here = String(pathname || "").replace(/\/+$/, "") || "/";
  // A `to` may carry a query string; the address is the path part of it.
  const target = String(to).split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return here === target;
};

/**
 * Returns an onClick builder for nav rows.
 *
 *   const onRowClick = useNavRefresh(onNavigate);
 *   <NavLink to={path} onClick={onRowClick(path)} />
 *
 * On a row you are NOT on, it just calls the caller's own handler (which is what
 * closes the mobile sheet) and lets the router navigate exactly as before. On
 * the row you ARE on, it cancels the dead click and reloads instead.
 */
const useNavRefresh = (onNavigate) => {
  const { pathname } = useLocation();

  return useCallback(
    (to) => (event) => {
      if (isSameScreen(pathname, to)) {
        // The router would have done nothing here anyway; preventDefault only
        // stops it adding a history entry for a navigation that is not happening.
        if (event) event.preventDefault();
        if (typeof window !== "undefined") window.location.reload();
        return;
      }
      if (onNavigate) onNavigate(event);
    },
    [pathname, onNavigate]
  );
};

export default useNavRefresh;
